// Models
import { IExerciseData } from 'storage/exercise/models'
import {
  IModelInput,
  IWorkoutSetInput,
  TFormValues,
  TMuscleGroupFilter,
  TSetSpecialSet,
  TSpecialSetInput,
} from 'models'

// React
import { FC, useLayoutEffect, useState } from 'react'

// Libraries
import { FormProvider, UseFormReturn } from 'react-hook-form'

// Misc
import { buttonClickTracking } from 'utils/tracking'
import { prepareSpecialWorkoutSetToState } from 'filters/workouts'
import { uid } from 'utils/functions'
import { useMenu, useModal } from 'hooks'

// Components
import * as Styled from './styled'
import { Aligner, SimpleContainer } from 'heeds-ds'
import {
  ExerciseItem,
  ModalExercises,
  ModalExerciseImage,
  ModalMuscleGroups,
  WorkoutSpecialSet,
} from 'components'

type Props = {
  addExerciseToWorkoutSet: (
    modelId: string,
    workoutSetId: string,
    exercise: IExerciseData,
  ) => void
  addWorkoutSet: (modelId: string, set: IWorkoutSetInput) => void
  changePage: (page: string) => void
  filters: TMuscleGroupFilter[]
  formMethods: UseFormReturn<TSpecialSetInput, TFormValues>
  inputValue: string
  loadMoreExercises: () => void
  onChangeFilters: (filters: TMuscleGroupFilter[]) => void
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  removeExerciseFromWorkoutSet: (
    modelId: string,
    workoutSetId: string,
    exerciseIndex: number,
  ) => void
  removeFilter: (label: string) => void
  selectedModelIndex: number
  setSpecialSet: TSetSpecialSet
  specialSet?: TSpecialSetInput
  stateModels: IModelInput[]
  updateWorkoutSet: (
    modelId: string,
    workoutSetId: string,
    updatedWorkoutSet: IWorkoutSetInput,
  ) => void
}

const WorkoutModelsSpecialSet: FC<Props> = ({
  addExerciseToWorkoutSet,
  addWorkoutSet,
  changePage,
  filters,
  formMethods,
  inputValue,
  loadMoreExercises,
  onChangeFilters,
  onSearchChange,
  removeExerciseFromWorkoutSet,
  removeFilter,
  selectedModelIndex,
  setSpecialSet,
  specialSet,
  stateModels,
  updateWorkoutSet,
}) => {
  const { handleSubmit, unregister } = formMethods
  const { closeModal, openModal, isVisible } = useModal()
  const { setPagename } = useMenu()

  const [selectedExercise, setSelectedExercise] = useState<
    IExerciseData | undefined
  >()

  const addExercise = (exercise: IExerciseData) => {
    const modelId = stateModels[selectedModelIndex].id
    const setId = uid()

    if (specialSet) {
      addExerciseToWorkoutSet(modelId, setId, exercise)
    }
  }

  const renderExercise = (exercise: IExerciseData) => {
    const hasInclude =
      specialSet?.exercises &&
      Object.values(specialSet?.exercises).find(
        (setExercise) => setExercise?.exercise?.name === exercise?.name,
      ) !== undefined
    return (
      <ExerciseItem
        checked={hasInclude}
        exercise={exercise}
        key={exercise.id}
        onClick={addExercise}
        onClickImage={openExerciseGifModal}
        shadow
      />
    )
  }

  const openExercisesModal = () => openModal('mobile-exercises-modal')

  const openExerciseGifModal = (exercise: IExerciseData) => {
    setSelectedExercise(exercise)
    openModal('mobile-exercise-gif-modal')
  }

  const openIncludeExerciseGifModal = (exercise: IExerciseData) => {
    setSelectedExercise(exercise)
    openModal('mobile-include-exercise-gif-modal')
  }

  const closeExerciseGifModal = () => {
    setSelectedExercise(undefined)
    if (isVisible === 'mobile-include-exercise-gif-modal') {
      return closeModal()
    }
    openExercisesModal()
  }

  const close = () => {
    if (specialSet) {
      unregister(['comments', 'exercises', 'interval', 'quantity'])
    }
    setSpecialSet(undefined)
    changePage('2')
  }

  const onSubmit = (formData: TSpecialSetInput) => {
    if (specialSet) {
      const modelId = stateModels[selectedModelIndex].id
      const updated = prepareSpecialWorkoutSetToState(formData, specialSet)
      specialSet.index !== undefined
        ? updateWorkoutSet(modelId, specialSet.id, updated)
        : addWorkoutSet(modelId, { ...updated, id: 'NEW' + uid() })
    }
    close()
  }

  useLayoutEffect(() => {
    setPagename('série especial')

    return () => setPagename('Dashboard')
  }, [setPagename])

  return (
    <FormProvider {...formMethods}>
      <Styled.Form onSubmit={handleSubmit(onSubmit)}>
        <SimpleContainer>
          <Aligner padding="24px">
            <WorkoutSpecialSet
              onCancel={close}
              onClickImage={openIncludeExerciseGifModal}
              onRemove={() => undefined}
              openExercisesModal={() => openModal('mobile-exercises-modal')}
              removeExerciseFromWorkoutSet={removeExerciseFromWorkoutSet}
              specialSet={specialSet}
            />
          </Aligner>

          <Styled.SaveButton
            track={buttonClickTracking}
            trackName="add_new_special_workout_set_and_navigate_to_mobile_workout_sets_in_workout_create"
          >
            Salvar série especial
          </Styled.SaveButton>

          {isVisible === 'mobile-exercises-modal' && (
            <ModalExercises
              filters={filters}
              inputValue={inputValue}
              loadMoreExercises={loadMoreExercises}
              onClickFilter={() => openModal('muscle-groups-modal')}
              onSearchChange={onSearchChange}
              removeFilter={removeFilter}
              renderExercise={renderExercise}
            />
          )}

          {(isVisible === 'mobile-exercise-gif-modal' ||
            isVisible === 'mobile-include-exercise-gif-modal') && (
            <ModalExerciseImage
              exercise={selectedExercise}
              handleClose={closeExerciseGifModal}
            />
          )}

          {isVisible === 'muscle-groups-modal' && (
            <ModalMuscleGroups
              filters={filters}
              handleClose={openExercisesModal}
              onChange={onChangeFilters}
            />
          )}
        </SimpleContainer>
      </Styled.Form>
    </FormProvider>
  )
}

export default WorkoutModelsSpecialSet
